import React, { FC, useMemo, useState } from 'react';
import { Instance } from 'mobx-state-tree';

import { PdfCourtFormStore } from '~/src/stores/pdfCourtFormsStore';
import StateSection from '~/src/components/CourtForms/StateSection';
import { DrillDownPanelButton } from '~/src/components/DrillDownPanel';
import PdfTableWrapper from './PdfTableWrapper';
import Breadcrumb from '~/src/components/Breadcrumb';
import type { BreadcrumbDataItem } from '~/src/components/Breadcrumb/Breadcrumb';
import useGetTemplateMetadata from '~/src/hooks/useGetTemplateMetadata';
import { Actions } from '~/src/components/RowActions/RowActions';
import { regional } from '~/src/regions/translations/regionalTranslations';
import {
  getFilterCategoriesForRegion,
  usesCountySubFilter,
  getSubFilterLabel,
} from '~/src/utils/additionalRegionForms';

interface PdfCourtForm extends Instance<typeof PdfCourtFormStore> {}

enum Level {
  FILTER_CATEGORIES = 'filterCategories',
  SUB_FILTER = 'subFilter',
  FORMS = 'forms',
}

interface Props {
  regionFilter?: string;
  rootLabel?: string;
  initialFilterCategory?: string;
  onBack?: () => void;
  disableAddButton?: boolean;
  rowActions?: Actions<PdfCourtForm>;
  handleAddTemplateToSeletionSidebar: (t: PdfCourtForm) => void;
  handlePreviewTemplate: (t: PdfCourtForm) => void;
  containedInSidebar: (t: PdfCourtForm) => boolean;
}

const RegionalCourtFormsWrapper: FC<Props> = ({
  regionFilter,
  rootLabel,
  initialFilterCategory,
  onBack,
  disableAddButton = false,
  rowActions,
  handleAddTemplateToSeletionSidebar,
  handlePreviewTemplate,
  containedInSidebar,
}) => {
  const { data: templateMetadata, isLoading } = useGetTemplateMetadata();

  const isCounty = usesCountySubFilter(regionFilter);
  const subFilterLabel = getSubFilterLabel(regionFilter);

  const [level, setLevel] = useState<Level>(
    initialFilterCategory ? Level.SUB_FILTER : Level.FILTER_CATEGORIES,
  );
  const [filterCategory, setFilterCategory] = useState(
    initialFilterCategory || '',
  );
  const [subFilterValue, setSubFilterValue] = useState('');

  const handleFilterCategorySelect = (category: string) => {
    setFilterCategory(category);
    setLevel(Level.SUB_FILTER);
  };

  const handleSubFilterSelect = (value: string) => {
    setSubFilterValue(value);
    setLevel(Level.FORMS);
  };

  const resetToStart = () => {
    if (initialFilterCategory && onBack) {
      onBack();
      return;
    }
    setLevel(Level.FILTER_CATEGORIES);
    setFilterCategory('');
    setSubFilterValue('');
  };

  const resetToSubFilter = () => {
    setLevel(Level.SUB_FILTER);
    setSubFilterValue('');
  };

  const filterCategories = useMemo(() => {
    if (!templateMetadata) return [];
    if (!regionFilter) {
      return Object.keys(templateMetadata.filter_categories || {});
    }
    return getFilterCategoriesForRegion(templateMetadata, regionFilter);
  }, [templateMetadata, regionFilter]);

  if (isLoading || !templateMetadata) {
    return <div>Loading...</div>;
  }

  const subFilterItems =
    filterCategory && templateMetadata.filter_categories
      ? templateMetadata.filter_categories[filterCategory] || []
      : [];

  const displayLabel = rootLabel || regional('courtForms');

  const rootClickHandler = initialFilterCategory
    ? onBack || undefined
    : level !== Level.FILTER_CATEGORIES
    ? resetToStart
    : onBack || undefined;

  const breadcrumbData: BreadcrumbDataItem[] = [
    {
      label: displayLabel,
      onClick: rootClickHandler,
    },
  ];

  if (filterCategory) {
    breadcrumbData.push({
      label: `${filterCategory} forms`,
      onClick: level === Level.FORMS ? resetToSubFilter : undefined,
    });
  }

  if (subFilterValue) {
    breadcrumbData.push({
      label: subFilterValue,
    });
  }

  if (level === Level.FILTER_CATEGORIES) {
    if (filterCategories.length === 0) {
      return (
        <div className="text-center mt-12 text-lg text-gray-500">
          No court forms available for your region.
        </div>
      );
    }

    return (
      <div>
        {filterCategories.map((category) => (
          <StateSection key={`${category}_section`} stateName={category}>
            <DrillDownPanelButton
              key={`${category}_button`}
              label={`${category} forms`}
              handleClick={() => handleFilterCategorySelect(category)}
            />
          </StateSection>
        ))}
      </div>
    );
  }

  if (level === Level.SUB_FILTER) {
    return (
      <div>
        <Breadcrumb data={breadcrumbData} />
        {subFilterItems.length === 0 ? (
          <div className="text-center mt-12 text-lg text-gray-500">
            No {(subFilterLabel || 'forms').toLowerCase()} available for{' '}
            {filterCategory}.
          </div>
        ) : (
          subFilterItems.map((item: string) => (
            <StateSection key={`${item}_section`} stateName={item}>
              <DrillDownPanelButton
                key={`${item}_button`}
                label={`${item} forms`}
                handleClick={() => handleSubFilterSelect(item)}
              />
            </StateSection>
          ))
        )}
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb data={breadcrumbData} />
      <PdfTableWrapper
        stateCode={filterCategory}
        practiceArea={isCounty ? undefined : subFilterValue}
        locality={isCounty ? subFilterValue : undefined}
        region={regionFilter}
        disableAddButton={disableAddButton}
        breadcrumbData={[]}
        rowActions={rowActions}
        handleAddTemplateToSeletionSidebar={handleAddTemplateToSeletionSidebar}
        handlePreviewTemplate={handlePreviewTemplate}
        containedInSidebar={containedInSidebar}
        disableBreadcrumb={true}
      />
    </div>
  );
};

export default RegionalCourtFormsWrapper;
